import {
  createContext,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { AudienceType, PartialShiftData, shiftSchema } from './schemas'
import { useQueryClient } from '@tanstack/react-query'
import {
  companyLocationsKey,
  companyMembersKey,
  companyPositionsKey,
} from 'queries/company/keys'
import {
  getCompanyLocations,
  getCompanyMembers,
  getCompanyPositions,
} from 'api/company'
import { ShiftEditData, ShiftSubmissionRequest } from 'api/shift'
import {
  fromServerData,
  ShiftStatusData,
  toServerData,
  getShiftStatusData,
  applyPositionBillRate,
} from './dataTransformers'
import {
  useSaveShiftMutation,
  useShiftForEdit,
  useShouldUseBillRate,
} from 'queries/shift'
import { Loading, toast } from 'ui'
import { format } from 'date-fns'
import { equals } from 'ramda'
import { useGateValue } from '@statsig/react-bindings'
import { FeatureGates } from 'lib/statsig/feature_gates'
import { useNavigateWithContext } from 'pages/ShiftEditorPage/useNavigateWithContext'
import { EditorMode, ShiftType, Step, StepEnum } from './types'
import { getStepsByShiftType } from './constants'
import { useHasPaymentMethod } from 'queries/payment/useHasPaymentMethod'
import { useAuthContext } from 'components/auth'
import { getShiftTypeForEdit } from './shiftType'
import { useIsDrivingPosition } from './pages/DetailsPage/useIsDrivingPosition'
import { getWorkerProfile } from 'api/company/worker/profile'

export interface ShiftEditorContextValues {
  shiftId?: number
  isNew: boolean
  isShiftSaved: boolean
  isTryout: boolean
  isAssignment: boolean
  isOnDemandDeliveryBlock: boolean
  editorMode: EditorMode
  shiftType: ShiftType
  shiftData: PartialShiftData
  shiftStatusData: ShiftStatusData
  steps: Step[]
  currentStep?: StepEnum
  goTo: (stepName: StepEnum, replace?: boolean) => void
  goNext: () => void
  setShiftType: (shiftType: ShiftType) => void
  setEditorMode: (editorMode: EditorMode) => void
  setShiftData: (data: PartialShiftData) => void
  hasRequiredData: () => boolean
  loadShiftData: (data: ShiftEditData) => void
  resetShiftData: () => void
  editorError?: string
  submitShiftData: () => void
  isSaving: boolean
  markStepSeen: (step: StepEnum) => void
  confirmationSeen: boolean
  workerToShiftLeadRatio: number
  hasPaymentMethod: boolean
  firstUnfinishedStep?: Step
  canGoTo: (stepName: StepEnum) => boolean
  isDirty: boolean
  editData?: ShiftEditData
}

const isDev = process.env.NODE_ENV !== 'production'

export const ShiftEditorContext =
  createContext<ShiftEditorContextValues | null>(null)

export const ShiftEditorProvider = (props: PropsWithChildren) => {
  const { children } = props
  const { shiftId: shiftIdParam } = useParams<{ shiftId: string }>()
  const shiftId =
    shiftIdParam && shiftIdParam !== 'new' ? Number(shiftIdParam) : undefined
  const { location, navigate } = useNavigateWithContext()
  const [searchParams] = useSearchParams()
  const [editorMode, setEditorMode] = useState(EditorMode.Standard)
  const [shiftType, setShiftType] = useState(ShiftType.Regular)
  const [shiftData, setShiftData] = useState<PartialShiftData>({})
  const [shiftStatusData, setShiftStatusData] = useState<ShiftStatusData>({
    workerStatus: {},
  })
  const isInternalCompany = useGateValue(FeatureGates.InternalCompanyTreatment)
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | undefined>()
  const [seenSteps, setSeenSteps] = useState<StepEnum[]>([])
  const [isLoadingWorkers, setLoadingWorkers] = useState(false)
  const referenceId = searchParams.get('reference_id')
    ? Number(searchParams.get('reference_id'))
    : undefined
  const hasPaymentMethod = useHasPaymentMethod()
  const { company } = useAuthContext()

  const steps = useMemo(() => {
    return getStepsByShiftType(shiftType)
  }, [shiftType])

  const firstUnfinishedStep = useMemo(() => {
    return steps.find((step) => {
      if (step.schemaKey && step.schema) {
        const data = shiftData[step.schemaKey]
        const allowZeroPay = isInternalCompany || shiftData.details?.isBillRate
        const { success } = step.schema.safeParse({
          ...data,
          ...(step.schemaKey === 'payment'
            ? {
                allowZeroPay,
              }
            : {}),
        })
        if (!success) {
          return true
        }
      }
      return false
    })
  }, [steps, shiftData, isInternalCompany])
  const [isDirty, setIsDirty] = useState(false)

  const { data: editData, isLoading: isLoadingShift } = useShiftForEdit({
    shiftId: shiftId ?? referenceId,
  })
  const editPositionId = editData?.position?.id
    ? Number(editData.position.id)
    : 0
  const isDrivingPosition = useIsDrivingPosition(editPositionId)
  const { data: billRateInfo, isLoading: isLoadingShouldUseBillRate } =
    useShouldUseBillRate(editPositionId)
  const isBillPayForPosition = billRateInfo?.isBillPay
  const shouldEnableOnDemandDeliveryShiftCreation = useGateValue(
    FeatureGates.EnableOnDemandDeliveryShiftCreation
  )

  useEffect(() => {
    if (!editData) {
      return
    }
    const clientData = fromServerData(editData, !referenceId)
    // only for editing existing shift
    if (shiftId) {
      const shiftStatusData = getShiftStatusData(editData)
      setShiftStatusData(shiftStatusData)
    }
    setShiftData((state) => ({ ...state, ...clientData }))

    if (referenceId) {
      goTo(StepEnum.Details, true)
    }
  }, [shiftId, referenceId, editData])

  // Runs for create-from-reference / new flows only. Once the per-position
  // signal resolves, patch the flag so the editor treats the shift as bill rate
  // (hides rate/markup, omits pay on save).
  useEffect(() => {
    if (shiftId) {
      // Editing an existing shift: bill-rate mode comes from the shift payload
      // (fromServerData: billRateId + billRate), not the position's latest
      // is_bill_pay.
      return
    }
    setShiftData((state) => {
      const details = applyPositionBillRate(
        state.details,
        editPositionId,
        isBillPayForPosition
      )
      return details === state.details ? state : { ...state, details }
    })
  }, [isBillPayForPosition, editPositionId, editData, shiftId])

  useEffect(() => {
    if (!editData) {
      return
    }

    if (!company && !editData.isTryout) {
      return
    }

    const nextType = getShiftTypeForEdit(
      editData,
      company?.w2 === true,
      isDrivingPosition
    )
    // FIXME: This is extremely bad / hacky and should be removed as soon as possible
    // Syncing react states within react world using useEffect is extremely bads
    if (
      !shouldEnableOnDemandDeliveryShiftCreation &&
      nextType === ShiftType.OnDemandDeliveryBlock
    ) {
      setShiftType(ShiftType.Regular)
    } else {
      setShiftType(nextType)
    }
  }, [
    editData,
    company,
    isDrivingPosition,
    shouldEnableOnDemandDeliveryShiftCreation,
  ])

  const {
    mutate: saveShift,
    isPending: isCreatingShift,
    isSuccess: isShiftSaved,
  } = useSaveShiftMutation({
    shiftId,
    isPast: editorMode === EditorMode.PastShift,
    onError(err) {
      setError(err.message)
    },
    onSuccess(shiftDate?: Date) {
      setIsDirty(false)
      const returnUrl = shiftDate
        ? `/home/${format(shiftDate, 'yyyy-MM-dd')}`
        : '/home'

      if (shiftType === ShiftType.LongTermAssignment) {
        toast.success('Assignment created successfully')
      } else {
        toast.success(
          shiftId ? 'Shift updated successfully' : 'Shift created successfully.'
        )
      }

      // wait to ensure block checker gets the latest isDirty
      setTimeout(() => {
        navigate(returnUrl, {}, true)
      }, 100)
    },
  })

  // prefetch some data
  useEffect(() => {
    queryClient.prefetchQuery({
      queryKey: companyLocationsKey,
      queryFn: getCompanyLocations,
    })
    queryClient.prefetchQuery({
      queryKey: companyPositionsKey,
      queryFn: getCompanyPositions,
    })
    queryClient.prefetchQuery({
      queryKey: companyMembersKey,
      queryFn: getCompanyMembers,
    })
  }, [])

  useEffect(() => {
    const scheduleMode = searchParams.get('schedule')
    const workerIds = searchParams.get('worker_ids')

    if (scheduleMode === 'past') {
      setEditorMode(EditorMode.PastShift)
      setShiftData((state) => ({
        ...state,
        staff: {
          numWorkers: 0,
          restrictShiftToAudience: true,
          audienceType: AudienceType.SpecificWorkers,
          supervisors: [],
        },
      }))
    }

    if (scheduleMode === 'future') {
      setShiftData((state) => ({
        ...state,
        staff: {
          numWorkers: 0,
          restrictShiftToAudience: false,
          audienceType: AudienceType.SpecificWorkers,
          supervisors: [],
        },
      }))
    }

    if (workerIds) {
      setLoadingWorkers(true)
      const ids = workerIds.split(',').map(Number)
      Promise.all(ids.map(getWorkerProfile)).then((workers) => {
        setShiftData((state) => ({
          ...state,
          staff: {
            numWorkers: workers.length,
            restrictShiftToAudience: false,
            audienceType: AudienceType.SpecificWorkers,
            supervisors: [],
            specificWorkers: workers.map((worker) => worker.worker),
          },
        }))
        setLoadingWorkers(false)
        goTo(StepEnum.Details, true)
      })
    } else if (scheduleMode) {
      goTo(StepEnum.Details, true)
    }
  }, [searchParams])

  const currentStep = useMemo(() => {
    return steps.find((step) => {
      if (
        location.pathname.split('/').pop() === shiftIdParam &&
        step.route === '/'
      ) {
        return true
      }

      return !!location.pathname.match(`(.+)${step.route}$`)
    })?.name
  }, [steps, shiftIdParam, location])

  const goTo = (stepName: StepEnum, replace = false) => {
    const step = steps.find((step) => step.name === stepName)
    if (step) {
      navigate(step.route, { replace })
    }
  }

  const hasRequiredData = () => {
    const { success } = shiftSchema.safeParse(shiftData)
    return success
  }

  const goNext = () => {
    if (!currentStep) {
      return navigate(steps[0].route)
    }

    // if all completed, go to Confirm
    if (hasRequiredData() && seenSteps.includes(StepEnum.Confirm)) {
      goTo(StepEnum.Confirm, true)
      return
    }

    const currentIndex = steps.findIndex((step) => step.name === currentStep)
    const nextStep = steps[currentIndex + 1]
    if (nextStep) {
      navigate(nextStep.route)
    }
  }

  const values = useMemo(
    () => ({
      shiftId,
      isNew: shiftIdParam === 'new',
      isShiftSaved,
      isTryout: shiftType === ShiftType.Tryout,
      isAssignment: shiftType === ShiftType.LongTermAssignment,
      isOnDemandDeliveryBlock: shiftType === ShiftType.OnDemandDeliveryBlock,
      editorMode,
      editorError: error,
      shiftType,
      shiftData,
      shiftStatusData,
      steps,
      currentStep,
      goTo,
      goNext,
      setShiftType,
      setEditorMode,
      setShiftData: (data: PartialShiftData) => {
        setShiftData((prev) => {
          const newData = { ...prev, ...data }
          const hasChange = !equals(prev, newData)
          setIsDirty(isDirty || hasChange)
          return newData
        })
      },
      resetShiftData: () => setShiftData({}),
      hasRequiredData,
      loadShiftData: (data: ShiftEditData) =>
        setShiftData(fromServerData(data)),
      submitShiftData: () => {
        if (!hasPaymentMethod) {
          setError(
            'You must add a payment method to be able to create a shift.'
          )
          return
        }

        const result = shiftSchema.safeParse(shiftData)
        if (!result.success) {
          setError(
            isDev
              ? result.error.toString()
              : 'Please double check all the information provided is correct.'
          )
          return
        }
        const submissionData: ShiftSubmissionRequest = toServerData(
          result.data,
          shiftType,
          shiftId
        )

        saveShift(submissionData)
      },
      isSaving: isCreatingShift,
      markStepSeen: (step: StepEnum) => {
        setSeenSteps((seenSteps) => {
          if (seenSteps.includes(step)) {
            return seenSteps
          }
          return [...seenSteps, step]
        })
      },
      confirmationSeen: seenSteps.includes(StepEnum.Confirm),
      workerToShiftLeadRatio: 20,
      hasPaymentMethod,
      firstUnfinishedStep,
      canGoTo: (step: StepEnum) => {
        const stepItem = steps.find((stepItem) => stepItem.name === step)
        if (!stepItem) {
          return false
        }
        return (
          !firstUnfinishedStep ||
          steps.indexOf(stepItem) <= steps.indexOf(firstUnfinishedStep)
        )
      },
      isDirty,
      editData,
    }),
    [
      shiftId,
      shiftIdParam,
      currentStep,
      editorMode,
      shiftType,
      shiftData,
      error,
      isCreatingShift,
      shiftStatusData,
      seenSteps,
      hasPaymentMethod,
      firstUnfinishedStep,
      isDirty,
      editData,
      isShiftSaved,
    ]
  )

  if (isLoadingShift || isLoadingShouldUseBillRate || isLoadingWorkers) {
    return <Loading />
  }

  if (shiftId && !shiftData.details) {
    return <Loading />
  }

  return (
    <ShiftEditorContext.Provider value={values}>
      {children}
    </ShiftEditorContext.Provider>
  )
}

export * from './types'
